import React, {
	createContext,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
	startTransition,
	useContext,
	useRef,
	useState,
} from 'react';

import noop from 'lodash/noop';

import type { GlobalSideNavigationProps } from '../../common/types';

type PeekMenuId = GlobalSideNavigationProps['peekMenuId'];

// While part of the same state, we provide `peekMenuId` and `setPeekMenuId` in separate contexts.
// Some components will depend on `peekMenuId`, which will change.
// Others will only depend on `setPeekMenuId`, which will never change.
export const PeekMenuIdContext: React.Context<string | undefined> =
	createContext<PeekMenuId>(undefined);
export const SetPeekMenuIdContext: React.Context<
	React.Dispatch<React.SetStateAction<string | undefined>>
> = createContext<Dispatch<SetStateAction<PeekMenuId>>>(noop);

export const usePeekMenuId = (): string | undefined => useContext(PeekMenuIdContext);
export const useSetPeekMenuId = (): React.Dispatch<React.SetStateAction<string | undefined>> =>
	useContext(SetPeekMenuIdContext);

type PeekMenuIdProviderProps = PropsWithChildren<{ value: PeekMenuId }>;

export const PeekMenuIdProviders = ({
	children,
	value,
}: PeekMenuIdProviderProps): React.JSX.Element => {
	// There are two ways for `peekMenuId` to change.
	// One is if the `peekMenuId` prop (i.e. `value`) changes, likely due to a route change.
	// The other is internally via `setPeekMenuId`, e.g. the more menu.
	// Each will override the other, the last one to change/call will be the current state.
	const [peekMenuId, setPeekMenuId] = useState(value);

	// Once FG is cleaned up, inline this hook
	useCustomSyncState({ setPeekMenuId, value });

	return (
		<PeekMenuIdContext.Provider value={peekMenuId}>
			<SetPeekMenuIdContext.Provider value={setPeekMenuId}>
				{children}
			</SetPeekMenuIdContext.Provider>
		</PeekMenuIdContext.Provider>
	);
};

interface UseCustomSyncStateParams {
	setPeekMenuId: (value: React.SetStateAction<string | undefined>) => void;
	value?: string;
}

// Sync peekMenuId with the prop during render instead of in a layout effect.
// This avoids hydration mismatches and the useLayoutEffect SSR warning.
// See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
function useCustomSyncState({ setPeekMenuId, value }: UseCustomSyncStateParams) {
	const prevValue = useRef(value);

	if (value !== prevValue.current) {
		prevValue.current = value;
		startTransition(() => {
			setPeekMenuId(value);
		});
	}
}
